<template>
    <!-- Desktop Table (visible only on 2xl screens and above) -->
    <div class="hidden 2xl:block overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-black">
          <tr>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Customer
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Brand
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Vehicle
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Tire Details
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Shop/Invoice
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Submitted
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-black divide-y divide-gray-200 dark:divide-gray-700">
          <tr
            v-for="tire in tires"
            :key="tire.tire_id"
            class="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-600 odd:bg-white even:bg-gray-25 dark:odd:bg-gray-900 dark:even:bg-gray-800"
          >
            <!-- Customer Name -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-center">
              <div class="text-xs sm:text-sm text-gray-900 dark:text-white font-medium">
                {{ tire.user?.first_name }} {{ tire.user?.last_name }}
              </div>
            </td>

            <!-- Brand -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-center">
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                :class="getBrandClass(tire.brand?.brand_name)"
              >
                {{ tire.brand?.brand_name || 'N/A' }}
              </span>
            </td>

            <!-- Vehicle -->
            <td class="px-3 sm:px-6 py-4 text-sm text-center text-gray-900 dark:text-white">
              <div class="font-medium text-xs sm:text-sm">{{ tire.vehicle?.vehicle_make }} {{ tire.vehicle?.vehicle_model }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">
                {{ tire.vehicle?.plate_number }}
              </div>
            </td>

            <!-- Tire Details -->
            <td class="px-3 sm:px-6 py-4 text-sm text-center text-gray-900 dark:text-white">
              <div class="font-medium text-xs sm:text-sm">{{ tire.pattern }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ tire.tire_size }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">DOT: {{ tire.dot }}</div>
            </td>

            <!-- Shop/Invoice -->
            <td class="px-3 sm:px-6 py-4 text-sm text-center text-gray-900 dark:text-white">
              <div class="text-xs sm:text-sm">{{ tire.shop }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ tire.invoice_number }}</div>
            </td>

            <!-- Submitted Date -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-center">
              <div class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {{ formatDate(tire.created_at) }}
              </div>
            </td>

            <!-- Status -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-center">
              <span
                class="inline-flex items-center px-2 sm:px-2.5 py-1 rounded-full text-xs font-medium"
                :class="getStatusClass(tire.approval_status)"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full mr-1 sm:mr-1.5"
                  :class="getStatusDotClass(tire.approval_status)"
                ></span>
                {{ tire.approval_status }}
              </span>
            </td>

            <!-- Actions -->
            <td class="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
              <div class="flex items-center justify-center space-x-1 lg:space-x-2">
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('view', tire)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    View Details
                  </div>
                </div>
                <div v-if="tire.approval_status === 'pending'" class="relative group inline-block">
                  <button
                    @click.stop="$emit('approve', tire)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Approve Tire
                  </div>
                </div>
                <div v-if="tire.approval_status === 'pending'" class="relative group inline-block">
                  <button
                    @click.stop="$emit('reject', tire)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Reject Tire
                  </div>
                </div>
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('delete', tire)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-red-700 dark:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Delete Tire
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Cards (visible below 2xl screens) -->
    <div class="block 2xl:hidden">
      <div class="divide-y divide-gray-200 dark:divide-gray-700">
        <div
          v-for="tire in tires"
          :key="tire.tire_id"
          class="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150"
        >
          <!-- Card Header -->
          <div class="flex items-start justify-between mb-4">
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                {{ tire.user?.first_name }} {{ tire.user?.last_name }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ tire.vehicle?.vehicle_make }} {{ tire.vehicle?.vehicle_model }} - {{ tire.vehicle?.plate_number }}
              </p>
            </div>
            <span
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2"
              :class="getStatusClass(tire.approval_status)"
            >
              <span
                class="w-1.5 h-1.5 rounded-full mr-1"
                :class="getStatusDotClass(tire.approval_status)"
              ></span>
              {{ tire.approval_status }}
            </span>
          </div>

          <!-- Card Body -->
          <div class="space-y-2 mb-4">
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 dark:text-gray-400">Brand:</span>
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="getBrandClass(tire.brand?.brand_name)"
              >
                {{ tire.brand?.brand_name || 'N/A' }}
              </span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 dark:text-gray-400">Pattern:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ tire.pattern }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 dark:text-gray-400">Size:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ tire.tire_size }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 dark:text-gray-400">DOT:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ tire.dot }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 dark:text-gray-400">Shop:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ tire.shop }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 dark:text-gray-400">Invoice:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ tire.invoice_number }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 dark:text-gray-400">Submitted:</span>
              <span class="text-gray-900 dark:text-white text-xs">{{ formatDate(tire.created_at) }}</span>
            </div>
          </div>

          <!-- Card Actions -->
          <div class="flex gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
            <button
              @click="$emit('view', tire)"
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
              </svg>
              View
            </button>
            <button
              v-if="tire.approval_status === 'pending'"
              @click="$emit('approve', tire)"
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
              Approve
            </button>
            <button
              v-if="tire.approval_status === 'pending'"
              @click="$emit('reject', tire)"
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
              Reject
            </button>
            <button
              @click="$emit('delete', tire)"
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-400 bg-white dark:bg-black hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium transition-colors"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
</template>

<script setup lang="ts">
import type { Tire } from '@/types/tire';

defineProps<{
  tires: Tire[];
  loading?: boolean;
}>();

defineEmits<{
  approve: [tire: Tire];
  reject: [tire: Tire];
  view: [tire: Tire];
  delete: [tire: Tire];
}>();

function getBrandClass(brandName?: string): string {
  if (brandName === 'Continental') {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
  } else if (brandName === 'Falken') {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  }
  return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    case 'approved':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-black/30 dark:text-gray-400';
  }
}

function getStatusDotClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500 dark:bg-yellow-400';
    case 'approved':
      return 'bg-green-500 dark:bg-green-400';
    case 'rejected':
      return 'bg-red-500 dark:bg-red-400';
    default:
      return 'bg-gray-500 dark:bg-gray-400';
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
</script>


